import React, { useState, useEffect } from "react";
import { api } from "@/auth/AuthContext";
import { Card } from "@/components/ui-kit";
import { toast } from "sonner";
import { Key, CheckCircle, LockSimple } from "@phosphor-icons/react";

export const StripeLiveKeyCard = ({ lang }) => {
  const cs = lang === "cs";
  const [status, setStatus] = useState(null);
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = () => api.get("/admin/stripe-key").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const activate = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/admin/stripe-key", { key: key.trim() });
      setKey("");
      setStatus((s) => ({ ...s, configured: true, livemode: data.livemode, masked: data.masked, account_id: data.account_id }));
      toast.success(cs ? "Ostré platby aktivní!" : "Live payments active!");
    } catch (e) {
      setError(e.response?.data?.detail || (cs ? "Aktivace selhala" : "Activation failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-6" data-testid="stripe-live-key-card">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h3 className="text-lg font-medium text-zinc-100 flex items-center gap-2">
          <Key weight="bold" size={18} className="text-amber-400" />
          {cs ? "Stripe LIVE klíč" : "Stripe LIVE key"}
        </h3>
        {status?.livemode ? (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-green-500/10 border border-green-500/30 text-green-400 text-xs font-mono uppercase tracking-wider" data-testid="stripe-live-active">
            <CheckCircle weight="fill" size={12} /> {cs ? "Ostré platby aktivní" : "Live payments active"}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-mono uppercase tracking-wider" data-testid="stripe-test-mode">
            {cs ? "Testovací režim" : "Test mode"}
          </span>
        )}
      </div>

      {status?.configured && (
        <div className="text-sm text-zinc-400 font-mono mb-3" data-testid="stripe-key-masked">
          {cs ? "Uložený klíč: " : "Stored key: "}{status.masked}
          {status.account_id && <span className="text-zinc-600"> · {status.account_id}</span>}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="password"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="sk_live_..."
          data-testid="stripe-key-input"
          className="flex-1 bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-zinc-100 font-mono placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/50"
        />
        <button
          onClick={activate}
          disabled={busy || !key.trim().startsWith("sk_live_")}
          data-testid="stripe-key-activate-btn"
          className="px-5 py-2 rounded-md text-sm font-medium bg-amber-500 hover:bg-amber-400 text-zinc-950 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {busy ? (cs ? "Ověřuji…" : "Verifying…") : (cs ? "Aktivovat" : "Activate")}
        </button>
      </div>

      {error && (
        <div className="text-sm text-red-400 mt-2" data-testid="stripe-key-error">{error}</div>
      )}

      <p className="text-xs text-zinc-500 mt-3 flex items-center gap-1.5">
        <LockSimple size={12} />
        {cs
          ? "Klíč vidí jen admin, ukládá se bezpečně a nikdy se nezobrazuje celý. Aktivace ověří klíč u Stripe a ihned vytvoří katalog i webhook."
          : "Admin-only. The key is stored securely and never shown in full. Activation verifies the key with Stripe and provisions the catalog and webhook instantly."}
      </p>
    </Card>
  );
};
